import { ActionFilters } from '../../types/filter-types'

import { attempt } from '../utils'
import { fetchNetlify } from '../netlify'
import { ActionToolAccountWithId } from '../../types/account-types'
import { SanitySuggestedActionResult } from '../../types/sanity-types'
import { removeDuplicates } from '../../utilities/array'
import {
  getAccountLocations,
  getHomeLocation,
  getSearchableLocationParams
} from '../../utilities/locations'
import { SanityFilter } from '../../utilities/sanity-filters'

export const fetchActions = async (slugs: string[]) => {
  return await fetchNetlify<Queries.SanityAction[]>(
    `/.netlify/functions/sanity-actions-by-slug?slugs=${slugs.join(',')}`
  )
}

export const fetchFilters = async (
  existing: ActionFilters = {},
  backgroundUpdate: (newFilters: ActionFilters) => void,
  specifiedFilters?: SanityFilter[]
) => {
  // NOTE: breaking each filter into separate request to prevent netlify
  // function timeouts
  const getFilter = async (filter: SanityFilter) =>
    await attempt(
      async () => {
        const data = await fetchNetlify<ActionFilters>(
          `/.netlify/functions/sanity-filters?${filter}=true`
        )
        return data
      },
      err => console.log(err || `Could not fetch ${filter} filters`)
    )

  let mutations = { ...existing }
  const updateInBackground = (filters?: ActionFilters) => {
    mutations = { ...mutations, ...filters }
    backgroundUpdate(mutations)
  }
  const shouldFetch = (filter: SanityFilter) => {
    return !specifiedFilters || specifiedFilters.length === 0 || specifiedFilters.includes(filter)
  }
  const toFetch = Object.values(SanityFilter).filter(filter => shouldFetch(filter))
  await Promise.all(
    toFetch.map(async filter => {
      const result = (await getFilter(filter))?.[filter]
      // A failed or timed-out request leaves whatever is already loaded in place
      // instead of replacing it with an empty list.
      if (result) {
        updateInBackground({ [filter]: result })
      }
    })
  )
}

export const fetchOtherVerbs = async (
  category: Queries.SanityCategory
): Promise<Queries.SanityVerb[] | undefined> => {
  const query = `category=${category.slug?.current}`
  return await fetchNetlify<Queries.SanityVerb[]>(
    `/.netlify/functions/sanity-other-verbs-than-category?${query}`
  )
}

export const fetchVerbsRelatedToNoun = async (
  nouns: Queries.SanityNoun[],
  category: Queries.SanityCategory
) => {
  const nounSlugs = nouns.map(noun => noun.slug?.current) as string[]
  const query = `nouns=${nounSlugs.join(',')}&category=${category.slug?.current}`
  return await fetchNetlify<Queries.SanityVerb[]>(
    `/.netlify/functions/sanity-related-verbs-to-noun?${query}`
  )
}

export const fetchRecentActions = async (count?: number, shuffle?: boolean) => {
  return await fetchNetlify<SanitySuggestedActionResult[]>(
    `/.netlify/functions/sanity-recent-actions?count=${count}&shuffle=${shuffle}`
  )
}

export const hasDataForPersonalizedActions = (account: ActionToolAccountWithId) => {
  if (!account?.user?.profile?.private) {
    return false
  }

  const { verbs, skills, cobenefits, nouns } = account.user.profile.private
  return [verbs, skills, cobenefits, nouns].some(arr => Array.isArray(arr) && arr.length > 0)
}

type RecommendedActionsMessage =
  | { type: 'ranked'; slugs: string[] }
  | { type: 'action'; slug: string; action: SanitySuggestedActionResult | null }
  | { type: 'done' }
  | { type: 'error'; message: string }

// Each entry is undefined while loading, null if that action failed, or the
// action. Always in ranked order.
export type PersonalizedActionsProgress = (SanitySuggestedActionResult | null | undefined)[]

// Personalized recommendations from the recommended-actions function, which
// streams the ranked order first and then each action as it's ready. onProgress
// is called on every update so the UI can fill cards in as they arrive; the
// promise resolves with the loaded actions in ranked order.
export const fetchPersonalizedActions = async (
  count: number,
  account: ActionToolAccountWithId,
  onProgress?: (actions: PersonalizedActionsProgress) => void
): Promise<SanitySuggestedActionResult[]> => {
  const { cobenefits, nouns, verbs, skills } = account?.user?.profile.private
  // Every saved location boosts matching actions; be heard actions with a
  // location are only recommended when they include My Location.
  const { cities, states, countries } = getSearchableLocationParams(getAccountLocations(account))
  const homeLocation = getHomeLocation(account)?.locationData
  const home = homeLocation
    ? { city: homeLocation.city, state: homeLocation.state, country: homeLocation.country }
    : null

  const exclude = removeDuplicates([
    ...(account?.user?.action_history?.map(action => action.action_slug) || []),
    ...(account?.user?.saved_actions || [])
  ])

  const response = await fetch('/.netlify/functions/recommended-actions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      count,
      verbs,
      nouns,
      cobenefits,
      skills,
      cities,
      states,
      countries,
      home,
      exclude
    })
  })

  if (!response.ok || !response.body) {
    throw new Error(`Could not load recommended actions (${response.status})`)
  }

  let slugs: string[] = []
  const actions = new Map<string, SanitySuggestedActionResult | null>()
  const progress = (): PersonalizedActionsProgress => slugs.map(slug => actions.get(slug))

  const handle = (message: RecommendedActionsMessage) => {
    if (message.type === 'error') {
      throw new Error(message.message)
    }
    if (message.type === 'ranked') {
      slugs = message.slugs
    } else if (message.type === 'action') {
      actions.set(message.slug, message.action)
    } else {
      return
    }
    onProgress?.(progress())
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffered = ''

  for (;;) {
    const { done, value } = await reader.read()
    buffered += decoder.decode(value, { stream: !done })

    const lines = buffered.split('\n')
    buffered = lines.pop() ?? ''
    lines.filter(line => line.trim()).forEach(line => handle(JSON.parse(line)))

    if (done) break
  }

  if (buffered.trim()) {
    handle(JSON.parse(buffered))
  }

  return progress().filter((action): action is SanitySuggestedActionResult => Boolean(action))
}
